import { type ProColumns } from '@ant-design/pro-components'
import { Button, Tag, Tooltip, Typography } from 'antd'
import { formatAmount } from '@/utils/money.ts'
import {
  BILL_COLLECTION_STATUS,
  BILL_KIND_COLORS,
  BILL_KINDS,
  BILL_STATUS,
  BILL_STATUS_COLORS
} from './const/bill-search.ts'
import type { BillRow } from './finance.types.ts'

export function createBillSummaryColumns({
  direction = 'receivable',
  searchableStatus = false,
  compact = false,
  onBill
}: {
  direction?: BillRow['direction']
  searchableStatus?: boolean
  compact?: boolean
  onBill?: (bill: BillRow) => void
} = {}): ProColumns<BillRow>[] {
  const isPayable = direction === 'payable'
  const amountWidth = compact ? '6.5rem' : '8rem'
  return [
    {
      title: 'ID',
      dataIndex: 'id',
      valueType: 'digit',
      search: false,
      copyable: true,
      width: compact ? '4.5rem' : '5.625rem'
    },
    {
      title: '费用类型',
      dataIndex: 'feeType',
      valueEnum: BILL_KINDS,
      width: compact ? '5.5rem' : '7rem',
      search: false,
      render: (_, row) => <Tag color={BILL_KIND_COLORS[row.feeType]}>{BILL_KINDS[row.feeType]}</Tag>
    },
    {
      title: '账单号',
      dataIndex: 'billNo',
      search: false,
      copyable: !onBill,
      width: '12rem',
      render: onBill
        ? (_, bill) => (
            <Typography.Text copyable={{ text: bill.billNo }}>
              <Button type="link" className="h-auto! p-0!" onClick={() => onBill(bill)}>
                {bill.billNo}
              </Button>
            </Typography.Text>
          )
        : undefined
    },
    {
      title: '账单状态',
      dataIndex: 'status',
      valueType: 'select',
      valueEnum: BILL_STATUS,
      search: searchableStatus ? undefined : false,
      fieldProps: { mode: 'multiple', allowClear: true, maxTagCount: 'responsive' },
      width: '6.25rem',
      render: (_, row) => <Tag color={BILL_STATUS_COLORS[row.status]}>{BILL_STATUS[row.status].text}</Tag>
    },
    {
      title: '结算状态',
      dataIndex: 'collectionStatus',
      valueType: 'select',
      valueEnum: BILL_COLLECTION_STATUS,
      search: searchableStatus ? undefined : false,
      fieldProps: { mode: 'multiple', allowClear: true, maxTagCount: 'responsive' },
      width: '8rem'
    },
    {
      title: '账单金额',
      tooltip: '原始金额加上已确认调整。草稿金额仅用于核对，出账后才能收付款。',
      dataIndex: 'totalAmount',
      align: 'right',
      search: false,
      width: amountWidth,
      renderText: (value: string | null) => (value === null ? '待确认' : `¥${formatAmount(value)}`)
    },
    {
      title: isPayable ? '待付' : '待收',
      dataIndex: 'outstandingAmount',
      align: 'right',
      search: false,
      width: amountWidth,
      tooltip: '应收账单的剩余待收金额；应付账单显示待付。金额未核定时显示待确认。',
      render: (_, row) =>
        row.totalAmount === null
          ? '待确认'
          : `${row.direction === 'payable' ? '待付 ' : ''}¥${formatAmount(row.outstandingAmount)}`
    },
    {
      title: isPayable ? '已付' : '已收',
      dataIndex: 'receivedAmount',
      align: 'right',
      search: false,
      width: amountWidth,
      tooltip: '累计已确认收款；应付账单显示已付。已退款在详情中单独记录。',
      render: (_, row) => `${row.direction === 'payable' ? '已付 ' : ''}¥${formatAmount(row.receivedAmount)}`
    },
    {
      title: isPayable ? '已收回' : '已退款',
      dataIndex: 'refundedAmount',
      align: 'right',
      search: false,
      width: amountWidth,
      tooltip: '已确认退款流水的累计金额，净收款 = 已收 - 已退款。',
      renderText: (value: string) => `¥${formatAmount(value)}`
    },
    {
      title: isPayable ? '待收回' : '待退',
      dataIndex: 'refundableAmount',
      align: 'right',
      search: false,
      width: amountWidth,
      tooltip: '净收款超出账单金额的部分；应付账单显示待收回。',
      render: (_, row) => `${row.direction === 'payable' ? '待收回 ' : ''}¥${formatAmount(row.refundableAmount)}`
    }
  ]
}

export function createBillNoteColumn({
  searchable = false,
  compact = false
}: { searchable?: boolean; compact?: boolean } = {}): ProColumns<BillRow> {
  return {
    title: '备注',
    dataIndex: 'note',
    search: searchable ? undefined : false,
    width: compact ? '8rem' : '10rem',
    ellipsis: true,
    fieldProps: { placeholder: '输入备注关键词', maxLength: 255 },
    render: (_, row) =>
      row.note ? (
        <Tooltip title={<span className="whitespace-pre-wrap">{row.note}</span>} trigger={['hover', 'focus']}>
          <span tabIndex={0} className={compact ? 'block w-28 truncate' : 'block w-36 truncate'}>
            {row.note}
          </span>
        </Tooltip>
      ) : (
        '—'
      )
  }
}
